import React, { useContext, useState } from 'react';
import moment from 'moment';
import { injectIntl } from 'react-intl';
import './style.scss';
import { formatNotificationTimestamp, getSenderInfo } from './helper';
import markAsUnReadIcon from '../../assets/icons/mark-as-unread.svg';
import markAsReadIcon from '../../assets/icons/mark-as-read.svg';
import { NotificationPanelContext } from '..';
import { withRouter } from 'react-router-dom';

const NotificationTemplate = ({ history, notification, recipient }) => {
  const {
    type = '',
    subType = '',
    createdAt = '',
    metadata = {},
    redirectUrlPath = ''
  } = notification || {};
  // const locale = intl?.locale || 'en';
  const formattedTimestamp = formatNotificationTimestamp(createdAt, 'en');
  const sender = getSenderInfo(notification);

  const { dispatch, service, list, updateNotification, setShowNotificationPanel } =
    useContext(NotificationPanelContext);


  // Get notification type translation key
  const typeKey = list.control[type]?.subTypes?.find((st) => st.key === subType)?.label;

  const { sentByType } = notification || {};

  const effectiveOtherParty = metadata?.otherParty || {};
  const effectiveCampaignTitle = metadata?.partnershipTitle || '';

  let chatTitle = '';
  if (sentByType === 'creator' || sentByType === 'client') {
    const parts = [
      sender?.name || '',
      effectiveOtherParty?.name || '',
      effectiveCampaignTitle || ''
    ].filter(Boolean); // remove empty values
    chatTitle = parts.join(' / ');
  } else if (sentByType === 'employee') {
    const parts = [sender?.name || '', effectiveCampaignTitle || ''].filter(Boolean);
    chatTitle = parts.join(' / ');
  }
  const showOtherPartyImage = !!effectiveOtherParty?.logo;
  const showSenderImage = !!sender?.image;

  let typeText = typeKey;

  const formatMonthDay = (dateValue) => {
    const m = moment(dateValue);
    if (!m.isValid()) {
      return '';
    }
    return m.format('MMM Do');
  };

  // Badge information from metadata
  let showBadge = false;

  if (
    metadata?.date?.length > 0 ||
    metadata?.status?.length > 0 ||
    metadata?.priority?.length > 0 ||
    (subType === 'task.type_changed' && metadata?.newTaskType?.length > 0)
  ) {
    showBadge = true;
  }

  const { isRead = false } = recipient || {};
  const [currentIcon, setCurrentIcon] = useState(isRead ? 'read' : 'unRead');

  const handleClick = async (e) => {
    e.stopPropagation();
    // Only do read logic if currently unread
    if (currentIcon === 'unRead') {
      const partnershipContentId = metadata?.partnershipContentId;
      // Chat "new message" notifications → mark whole conversation as read
      if (
        (subType === 'chat.new_message' || subType === 'chat.assigned') &&
        partnershipContentId
      ) {
        try {
          await service.readChatConversationNotifications({ partnershipContentId });
          // Optional: optimistic local state update for this card
          setCurrentIcon('read');
        } catch (error) {
          console.error('read chat conversation notifications error', error);
        }
      } else {
        // Non-chat (or chat without partnershipContentId) → existing single-toggle behavior
        await handleHoverIconClick(e);
      }
    }

    // Existing redirect behavior
    if (redirectUrlPath?.length) {
      let correctedPath = redirectUrlPath;
      if (redirectUrlPath.includes('token=')) {
        if (redirectUrlPath.startsWith('/requests/') || redirectUrlPath.startsWith('/requests?')) {
          correctedPath = redirectUrlPath.replace(/^\/requests/, '/request');
        }
      }
      
      history.push(correctedPath);

      dispatch(setShowNotificationPanel(false));
    }
  };

  // State to track which icon is currently shown (toggles on click)

  // Determine current icon source and alt text based on state
  const hoverIconSrc = currentIcon === 'unRead' ? markAsReadIcon : markAsUnReadIcon;
  const hoverIconAlt = currentIcon === 'unRead' ? 'Mark as read' : 'Mark as unread';

  const handleHoverIconClick = async (event) => {
    event.stopPropagation();

    const oldNotification = notification;

    dispatch(
      updateNotification({
        ...notification,
        recipients: notification.recipients.map((r) =>
          r.id === recipient.id
            ? {
                ...recipient,
                isRead: currentIcon !== 'read',
                readAt: currentIcon !== 'read' ? new Date() : null
              }
            : r
        )
      })
    );

    // Toggle between the two icons
    const newIcon = currentIcon === 'unRead' ? 'read' : 'unRead';
    setCurrentIcon(newIcon);

    await service.toggleReadNotification({ id: notification.id }).catch((error) => {
      console.error('toggle read notification error', error);
      dispatch(updateNotification(oldNotification));
    });
  };

  // Fallback placeholder image
  const placeholderImage =
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNEMUQ1REIiLz4KPHBhdGggZD0iTTIwIDEyQzIyLjIwOTEgMTIgMjQgMTMuNzkwOSAyNCAxNkMyNCAxOC4yMDkxIDIyLjIwOTEgMjAgMjAgMjBDMTcuNzkwOSAyMCAxNiAxOC4yMDkxIDE2IDE2QzE2IDEzLjc5MDkgMTcuNzkwOSAxMiAyMCAxMloiIGZpbGw9IiM5NDk4OEUiLz4KPHBhdGggZD0iTTIwIDIyQzE1LjU4MTcgMjIgMTIgMjQuNTgxNyAxMiAyOVYzMEgyOFYyOUMyOCAyNC41ODE3IDI0LjQxODMgMjIgMjAgMjJaIiBmaWxsPSIjOTQ5ODhFIi8+Cjwvc3ZnPgo=';

  const renderBadge = () => {
    let badgeText = null;
    let badgeClass = '';

    if (metadata?.date?.length > 0) {
      if (['task.expiring'].includes(subType)) {
        badgeClass += 'max';
      } else {
        badgeClass += 'backlog';
      }
      badgeText = formatMonthDay(metadata?.date);
    } else if (metadata?.status?.length > 0) {
      badgeText = metadata?.status;
      badgeClass += badgeText?.trim()?.toLowerCase();
    } else if (metadata?.priority?.length > 0) {
      badgeText = metadata?.priority;
      badgeClass += badgeText?.trim()?.toLowerCase();
    } else if (subType === 'task.type_changed' && metadata?.newTaskType?.length > 0) {
      badgeText = metadata.newTaskType;
      badgeClass += badgeText?.trim()?.toLowerCase();
    }

    if (!badgeClass.length) {
      badgeClass = 'default';
    }

    return (
      <>
        <span className="notification-card__type-arrow"> &rarr; </span>
        <span className={`notification-card__badge notification-card__badge--${badgeClass}`}>
          {badgeText}
        </span>
      </>
    );
  };



  return (
    <div
      className={`notification-card ${!isRead ? 'notification-card--unread' : ''}`}
      onClick={handleClick}
    >
      <div className="notification-card__header">
        <div className="notification-card__type-wrapper">
          <span className="notification-card__type">{typeText}</span>
          {showBadge && renderBadge()}
        </div>
        <div className="notification-card__timestamp-wrapper">
          <span className="notification-card__timestamp">{formattedTimestamp}</span>
          {currentIcon === 'unRead' && <span className="notification-card__unread-dot"></span>}
        </div>
      </div>

      <div className="notification-card__body">
        <div
          className={`notification-card__images-wrapper
          ${showSenderImage && showOtherPartyImage ? 'notification-card__images-wrapper--double' : ''}`}
        >
          {showSenderImage && (
          <div className="notification-card__image-container notification-card__image-container--primary">
            {sender?.name && (
              <img
                src={sender?.image || placeholderImage}
                alt={sender?.name || 'User'}
                className="notification-card__image"
                onError={(e) => {
                  e.target.src = placeholderImage;
                }}
              />
            )}
          </div>
          )}
          {showOtherPartyImage && (
            <div className="notification-card__image-container notification-card__image-container--secondary">
              <img
                src={effectiveOtherParty?.logo || placeholderImage}
                alt={effectiveOtherParty?.name || 'Other party'}
                className="notification-card__image"
                onError={(e) => {
                  e.target.src = placeholderImage;
                }}
              />
            </div>
          )}
        </div>

        <div className="notification-card__content">
          {subType === 'chat.new_message' ? (
            <div className="notification-card__user">{chatTitle}</div>
          ) : (
            <div className="notification-card__user">{sender?.name || '-'}</div>
          )}
          <div className="notification-card__details-wrapper">
            <div className="notification-card__details">{metadata.description}</div>
            <button
              type="button"
              className="notification-card__hover-icon"
              onClick={handleHoverIconClick}
              aria-label={hoverIconAlt}
            >
              <img src={hoverIconSrc} alt={hoverIconAlt} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const NotificationTemplateWithIntl = withRouter(injectIntl(NotificationTemplate));
export { NotificationTemplateWithIntl as NotificationTemplate };
